import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, ProgressBar } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import FormContainer from '../components/FormContainer';
import Loader from '../components/Loader';
import VerifyEmailModal from '../components/User/VerifyEmailModal';
import { useRegisterMutation } from '../slices/usersApiSlices';

const RegisterScreen = () => {
  const [name, setName] = useState(localStorage.getItem("register_form_name") || '');
  const [email, setEmail] = useState(localStorage.getItem("register_form_email") || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false)

  const [errorMessage, setErrorMessage] = useState('')

  const navigate = useNavigate();

  const [register, { isLoading }] = useRegisterMutation();

  const { userInfo } = useSelector((state) => state.auth);

  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const redirect = sp.get('redirect') || '/SquadDataScreen';
  const termsSections = [
    {
      title: 'Information',
      content:
        'The ShotARC web application is developed by Dr Kevin McDaid at Dundalk Institute of Technology (DkIT) as part of a research project to improve prediction models for shot success in Gaelic Football. This application is available free of charge. DkIT will not be responsible for any loss of information associated with this application.',
    },
    {
      title: 'Agreement',
      content:
        'All data collected through this application will be used, on an anonymous basis, to research and improve existing statistical prediction models. The data will only be used for this purpose and no individual teams will be identified. It is recommended that you use anonymised names for your players.',
    },
    {
      title: 'Participation',
      content:
        'If you wish to no longer participate in the project please email kevin.mcdaid@dkit.ie.',
    },
  ];


  // Redirect user if already logged in
  useEffect(() => {
    if (userInfo ) {
      navigate(redirect);
    }
  }, [userInfo, redirect, navigate]);

  // Password strength scoring function
  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score += 20;
    if (/[A-Z]/.test(pwd)) score += 20;
    if (/[a-z]/.test(pwd)) score += 20;
    if (/\d/.test(pwd)) score += 20;
    if (/[!@#$%^&*.]/.test(pwd)) score += 20;  // Accepts . without escaping it
    return score;
  };

  // Determine progress bar variant based on score
  const getPasswordVariant = (score) => {
    if (score < 40) return 'danger';
    if (score < 80) return 'warning';
    return 'success';
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    // Modified regex to include dot (.) as a special character (without escaping the dot).
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*.])[A-Za-z\d!@#$%^&*.]{8,}$/;
    if (!passwordRegex.test(password)) {
      //toast.error(
      //  'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (e.g., !@#$%^&*.).'
      //);
      setErrorMessage('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (e.g., !@#$%^&*.).')
      return;
    }

    if (password !== confirmPassword) {
      //toast.error('Passwords do not match');
      setErrorMessage("Passwords do not match.")
      return;
    }

    if (!agreedToTerms){
      //toast.error('Please agree to the terms of use');
      setErrorMessage("Please agree to the terms of use.")
      return;
    }
    try {
      await register({ name, email: normalizedEmail, password, agreedToTerms }).unwrap();
      localStorage.removeItem("register_form_name")
      localStorage.removeItem("register_form_email")
      setEmail(normalizedEmail)
      setShowVerifyEmailModal(true)
      //dispatch(setCredentials(res));
      //navigate(redirect);
    } catch (error) {
      //toast.error(error?.data?.message || error.error);
      setErrorMessage(error?.data?.message || error.error)
    }
  };

  // Calculate password strength score for progress bar
  const strengthScore = getPasswordStrength(password);
  const handleVerifyModalClose = () => {
    setShowVerifyEmailModal(false);
    navigate('/LoginScreen');
  };

  return (
    <>
    <FormContainer>
      <title>Register | ShotArc</title>
      <div className="auth-page-wrap">
      <div className="auth-panel">
        <h1 className="auth-title">Sign Up</h1>
        <p className="auth-subtitle">Create an account to start tracking and analyzing shots.</p>
        <Form onSubmit={submitHandler}>
          <Form.Group controlId="name" className="my-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            ></Form.Control>
          </Form.Group>

          <Form.Group controlId="email" className="my-3">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              required
            ></Form.Control>
          </Form.Group>

          <Form.Group controlId="password" className="my-3">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            ></Form.Control>
            {/* Display progress bar when user starts typing without showing the score */}
            {password && (
              <ProgressBar
                now={strengthScore}
                variant={getPasswordVariant(strengthScore)}
                className="mt-2 auth-progress"
              />
            )}
          </Form.Group>

          <Form.Group controlId="confirmPassword" className="my-3">
            <Form.Label>Confirm Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            ></Form.Control>
          </Form.Group>

          <Form.Group className="my-3">
            <Form.Label className="mb-2">Terms of Use</Form.Label>
            <div className="auth-terms-scroll" role="region" aria-label="Terms of Use">
              {termsSections.map((section) => (
                <div key={section.title} className="auth-terms-section">
                  <h6 className="auth-terms-title">{section.title}</h6>
                  <p className="mb-0">{section.content}</p>
                </div>
              ))}
            </div>
          </Form.Group>

          <Form.Group className="d-flex align-items-center auth-terms-row">
              <Form.Label className="mb-0">
                I have read and agree to the terms of use
              </Form.Label>
              <Form.Check
                type="checkbox"
                checked={agreedToTerms}
                className="ms-2"
                onChange={e=>{setAgreedToTerms(e.target.checked)}}
                required
              />
          </Form.Group>

          <p className="text-danger mt-3">{errorMessage || ''}</p>

          <Button type="submit" variant="warning" className="mt-1 w-100 fw-semibold auth-submit-btn" disabled={isLoading}>
            Register
          </Button>

          {isLoading && <Loader />}
        </Form>
        <div className="auth-divider" />
        <Row className="pt-2">
          <Col>
            <span className="auth-helper-text">Already have an account?</span>{' '}
            <Link to={redirect ? `/LoginScreen?redirect=${redirect}` : '/LoginScreen'} className="auth-inline-link fw-semibold">
              Login
            </Link>
          </Col>
        </Row>
        <Row className="pt-2">
          <Col>
            <span className="auth-helper-text">Want to learn more?</span>{' '}
            <Link to="/WhatIsShotArc" className="auth-inline-link fw-semibold">
              What is ShotArc?
            </Link>
          </Col>
        </Row>
      </div>
      </div>
    </FormContainer>

    <VerifyEmailModal 
      show={showVerifyEmailModal}
      email={email}
      onHide={handleVerifyModalClose}
    />
    </>
  );
};

export default RegisterScreen;
